import { useState } from 'react';
import { X, Layout, Zap, Target, Users } from 'lucide-react';
import { FUNNEL_TEMPLATES } from '../../../data/funnelTemplates';

const STAGE_COLORS = {
    TOFU: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6', label: 'TOFU' },
    MOFU: { bg: 'rgba(168, 85, 247, 0.1)', color: '#A855F7', label: 'MOFU' },
    BOFU: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22C55E', label: 'BOFU' }
};

const DIFFICULTY_ICONS = {
    beginner: { icon: Zap, label: 'Beginner', color: 'var(--success-600)' },
    intermediate: { icon: Target, label: 'Intermediate', color: 'var(--warning-600)' },
    advanced: { icon: Users, label: 'Advanced', color: 'var(--error-600)' }
};

export default function TemplateGallery({ isOpen, onClose, onSelectTemplate }) {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [filter, setFilter] = useState('all');

    if (!isOpen) return null;

    const filteredTemplates = filter === 'all'
        ? FUNNEL_TEMPLATES
        : FUNNEL_TEMPLATES.filter(t => t.stages.includes(filter));

    const handleSelect = () => {
        if (selectedTemplate) {
            onSelectTemplate(selectedTemplate);
            onClose();
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                width: '90%',
                maxWidth: '900px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <Layout size={24} style={{ color: 'var(--color-primary)' }} />
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                                Funnel Templates
                            </h2>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                Start with a pre-built funnel structure
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-tertiary)',
                            borderRadius: 'var(--radius-sm)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Filters */}
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    {['all', 'TOFU', 'MOFU', 'BOFU'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            style={{
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                borderRadius: 'var(--radius-full)',
                                backgroundColor: filter === f
                                    ? f === 'all' ? 'var(--color-primary)' : STAGE_COLORS[f]?.bg
                                    : 'transparent',
                                color: filter === f
                                    ? f === 'all' ? 'white' : STAGE_COLORS[f]?.color
                                    : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: filter === f ? 600 : 400,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {f === 'all' ? 'All Templates' : f}
                        </button>
                    ))}
                </div>

                {/* Template Grid */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-6)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 'var(--space-4)'
                }}>
                    {filteredTemplates.map(template => {
                        const isSelected = selectedTemplate?.id === template.id;
                        const DifficultyIcon = DIFFICULTY_ICONS[template.difficulty]?.icon || Zap;

                        return (
                            <div
                                key={template.id}
                                onClick={() => setSelectedTemplate(template)}
                                style={{
                                    padding: 'var(--space-4)',
                                    border: isSelected
                                        ? '2px solid var(--color-primary)'
                                        : '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-lg)',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? 'var(--primary-50)' : 'var(--color-surface)',
                                    transition: 'all 0.2s ease'
                                }}
                                className="template-card"
                            >
                                {/* Template Name */}
                                <h3 style={{
                                    margin: '0 0 var(--space-2) 0',
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}>
                                    {template.name}
                                </h3>

                                {/* Description */}
                                <p style={{
                                    margin: '0 0 var(--space-3) 0',
                                    fontSize: '0.8rem',
                                    color: 'var(--color-text-secondary)',
                                    lineHeight: 1.4
                                }}>
                                    {template.description}
                                </p>

                                {/* Stage badges */}
                                <div style={{
                                    display: 'flex',
                                    gap: 'var(--space-1)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    {template.stages.map(stage => (
                                        <span
                                            key={stage}
                                            style={{
                                                padding: '2px 8px',
                                                backgroundColor: STAGE_COLORS[stage]?.bg,
                                                color: STAGE_COLORS[stage]?.color,
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: '0.65rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {stage}
                                        </span>
                                    ))}
                                </div>

                                {/* Stats */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontSize: '0.75rem',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    <span>
                                        {template.nodes.filter(n => n.type === 'campaign').length} campaign(s),{' '}
                                        {template.nodes.filter(n => n.type === 'ad').length} ad(s)
                                    </span>
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        color: DIFFICULTY_ICONS[template.difficulty]?.color
                                    }}>
                                        <DifficultyIcon size={12} />
                                        {DIFFICULTY_ICONS[template.difficulty]?.label}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--space-4) var(--space-6)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                        {selectedTemplate
                            ? `Selected: ${selectedTemplate.name}`
                            : 'Select a template to get started'
                        }
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: 'var(--space-2) var(--space-4)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSelect}
                            disabled={!selectedTemplate}
                            style={{
                                padding: 'var(--space-2) var(--space-4)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: selectedTemplate ? 'var(--color-primary)' : 'var(--color-border)',
                                color: selectedTemplate ? 'white' : 'var(--color-text-tertiary)',
                                cursor: selectedTemplate ? 'pointer' : 'not-allowed',
                                fontSize: '0.875rem',
                                fontWeight: 500
                            }}
                        >
                            Use Template
                        </button>
                    </div>
                </div>

                <style>{`
                    .template-card:hover {
                        border-color: var(--color-primary) !important;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    }
                `}</style>
            </div>
        </div>
    );
}
